import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { initializedActiveZones } from "../../interfaces/cleaning-map/active-zones";
import MapInfo from "../../interfaces/cleaning-map/map-info";
import RobotPosition from "../../interfaces/cleaning-map/robot-position";
import RobotVersion, { getRobotWidthFromVersion } from "../../interfaces/cleaning-map/robot-version";
import Polygon from "../../interfaces/cleaning-map/zones/polygon";
import Point, { getSvgCoordinatesFromSimplePoint } from "../../interfaces/robot/rooms/point";
import { useSharedSelector } from "../../store/SharedProvider";
import {
  selectCurrentRobotCurrentRoom,
  selectCurrentRobotCurrentRoomRedZones,
  selectFloorsRestSet,
} from "../../store/room/room-selectors";
import ActiveZonesCanvas from "./ActiveZonesCanvas";
import CoverageCanvas from "./CoverageCanvas";
import KRobotSvg from "./KRobotSvg";
import StationSvg from "./StationSvg";
import CircularProgress from "@mui/material/CircularProgress/CircularProgress";
import Base64Image from "../misc/Base64Image";
import { selectCurrentRobotCoverageMap, selectCurrentRobotPosition, selectCurrentRobotState } from "../../store/status/status-selectors";
import { selectRobotModel } from "../../store/robot-configurations/robot-configurations-selectors";
import Alert from "@mui/material/Alert";
import RswState from "../../enums/robot/rsw/rsw-state";
import HttpFetchStatus from "../../enums/api/http-fetch-status";

export interface StaticMapChildrenProps {
  onLoad: () => void;
  onError: () => void;
}

const StaticMap: React.FC<{ onMapLoadingError?: () => void }> = ({ onMapLoadingError }) => {
  const { t } = useTranslation();
   const [mapLoadingError, setMapLoadingError] = useState(false);

  const roomData = useSharedSelector((state) => selectCurrentRobotCurrentRoom(state));
  const { getStatus: getDataStatus } = useSharedSelector((state) => selectFloorsRestSet(state));
  const redZones = useSharedSelector((state) => selectCurrentRobotCurrentRoomRedZones(state));
  const robotPosition = useSharedSelector((state) => selectCurrentRobotPosition(state));
  const coverageMap = useSharedSelector((state) => selectCurrentRobotCoverageMap(state));
  const robotModel = useSharedSelector((state) => selectRobotModel(state));
  const currentRobotState = useSharedSelector((state) => selectCurrentRobotState(state));

  const isLoading = useMemo(() => {
    const robotStateLoading = currentRobotState?.status == null ||
      [
        RswState.InitSystem,
        RswState.InitMap,
        RswState.InitSystemSimulation,
        RswState.Unknown,
      ].includes(currentRobotState.status.state);

    return robotStateLoading;
  }, [currentRobotState?.status]);

  const coverageMapInfo = useMemo(() => coverageMap, [coverageMap]);
  const primaryMapInfo = useMemo(
    () =>
      roomData?.mapInformation != null
        ? ({
          width: roomData.mapInformation.width,
          height: roomData.mapInformation.height,
          resolution: roomData.mapInformation.resolution,
          originX: roomData.mapInformation.xPosition,
          originY: roomData.mapInformation.yPosition,
          map: "",
        } as MapInfo)
        : null,
    [roomData?.mapInformation]
  );

  const robotRadius = useMemo(() => {
    return Math.round(
      getRobotWidthFromVersion(robotModel ?? RobotVersion.K900) / ((roomData?.mapInformation.resolution ?? 1) * 2)
    );
  }, [roomData?.mapInformation.resolution, robotModel]);

  // rounding is necesssary to avoid excessive re-renders due to floating point precision issues
  const robotPositionX = robotPosition ? Math.round(robotPosition.x * 1000) / 1000 : null;
  const robotPositionY = robotPosition ? Math.round(robotPosition.y * 1000) / 1000 : null;
  const robotPositionTheta = robotPosition ? Math.round(robotPosition.theta * 1000) / 1000 : null;

  const robotPositionMemo = useMemo(() => {
    if (robotPositionX != null && robotPositionY != null && robotPositionTheta != null) {
      return {
        x: robotPositionX,
        y: robotPositionY,
        theta: robotPositionTheta,
        coverageMode: robotPosition?.coverageMode ?? 0,
      } as RobotPosition;
    }

    return null;
  }, [robotPosition?.coverageMode, robotPositionTheta, robotPositionX, robotPositionY]);

  const robotSvgCoordinates = useMemo(() => {
    if (robotPositionX != null && robotPositionY != null && robotPositionTheta != null) {
      const robotPositionSvg = getSvgCoordinatesFromSimplePoint(
        { x: robotPositionX, y: robotPositionY },
        roomData ?? null
      );

      return robotPositionSvg != null ? { ...robotPositionSvg, theta: robotPositionTheta } : null;
    }

    return null;
  }, [robotPositionX, robotPositionY, robotPositionTheta, roomData]);

  const stationSvgCoordinates = useMemo(() => {
    if (roomData?.primaryStation.coordinateX != null && roomData?.primaryStation.coordinateY != null) {
      return getSvgCoordinatesFromSimplePoint(
        { x: roomData?.primaryStation.coordinateX, y: roomData?.primaryStation.coordinateY },
        roomData
      );
    }

    return null;
  }, [roomData]);

  const activeRedZones = useMemo(() => {
    const redZonePolygons = redZones
      .filter((rz) => !rz.evacuation)
      .map(
        (rz) =>
        ({
          points: rz.points.map(
            (p) =>
            ({
              coordinateX: p.point.coordinateX,
              coordinateY: p.point.coordinateY,
              coordinateTheta: p.point.coordinateTheta,
            } as Point)
          ),
        } as Polygon)
      );

    return initializedActiveZones({ restrictedZones: redZonePolygons });
  }, [redZones]);

  return (
    <React.Fragment>
      <Box sx={{ position: "relative" }}>
        {roomData != null && (
          <Base64Image
            imageString={roomData.map}
            alt={""}
            onError={() => {
              setMapLoadingError(true);
              onMapLoadingError?.();
            }}
            // change the background color of the map to match colors in web-app
            colorSwap={{ from: [205, 205, 205], to: [242, 244, 247] }}
          />
        )}

        {!isLoading && roomData != null && (
          <React.Fragment>
            {primaryMapInfo != null && (
              <CoverageCanvas
                coverageMapInfo={coverageMapInfo}
                primaryMapInfo={primaryMapInfo}
                robotPosition={robotPositionMemo}
                robotRadius={robotRadius}
                canvasStyle={{ position: "absolute", top: 0, left: 0 }}
              />
            )}

            <svg
              viewBox={`0 0 ${roomData.mapInformation.width} ${roomData.mapInformation.height}`}
              style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
            >
              {stationSvgCoordinates != null && (
                <StationSvg
                  coordinateX={stationSvgCoordinates.x}
                  coordinateY={stationSvgCoordinates.y}
                  coordinateTheta={0}
                />
              )}
              {robotSvgCoordinates != null && (
                <KRobotSvg
                  coordinateX={robotSvgCoordinates.x}
                  coordinateY={robotSvgCoordinates.y}
                  coordinateTheta={robotSvgCoordinates.theta}
                  robotRadius={robotRadius}
                  model={robotModel ?? RobotVersion.K900}
                />
              )}
            </svg>
          </React.Fragment>
        )}

        {!isLoading && activeRedZones != null && primaryMapInfo != null && (
          <ActiveZonesCanvas
            activeZones={activeRedZones}
            primaryMapInfo={primaryMapInfo}
            noPadding
            onLoaded={() => {}}
          />
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
        }}
      >
        {isLoading && <CircularProgress />}
        {(mapLoadingError ||
          (!isLoading &&
            !roomData &&
            (getDataStatus === HttpFetchStatus.Success || getDataStatus === HttpFetchStatus.Failed))) && (
          <Alert severity="warning">{t("dashboard-cannot-load-map")}</Alert>
        )}
      </Box>
    </React.Fragment>
  );
};

export default StaticMap;
